import { useState } from 'react';
import { ArrowRight, Link2, Trash2, Warehouse } from 'lucide-react';
import { toast } from '@/stores/toast-store';
import { isWarehouseType } from '@/lib/location-type';
import {
  useAdminPrivateWarehouses,
  useAdminPrivateWarehouseMappings,
  useCreatePrivateWarehouseMapping,
  useDeactivatePrivateWarehouseMapping,
  useAdminProductMappings,
  useAdminBarcodePreview,
  useAdminProductSearch,
  useUpsertProductMapping,
  useDeleteProductMapping,
  type PrivateWarehouseMapping,
} from '@/hooks/use-admin-private-warehouses';

const errorMessage = (e: unknown, fallback: string) =>
  (e as { error?: { message?: string } })?.error?.message || fallback;

const selectCls =
  'h-9 rounded-lg border border-input bg-white px-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary';

/**
 * Super-admin card on the tenant detail page.
 *   • Sources from — this tenant's store locations mapped to other tenants'
 *     private warehouses (create here), plus the product mappings per pair.
 *   • Supplies — other tenants' locations mapped to this tenant's private
 *     warehouses (created from the buyer tenant's page; deactivate here).
 */
export function PrivateWarehouseMappingsCard({
  tenantId,
  locations,
}: {
  tenantId: string;
  locations: { id: string; name: string; type: string }[];
}) {
  const { data: mappingData } = useAdminPrivateWarehouseMappings(tenantId);
  const { data: warehouseData } = useAdminPrivateWarehouses();
  const createMapping = useCreatePrivateWarehouseMapping();
  const deactivateMapping = useDeactivatePrivateWarehouseMapping();

  const mappings = mappingData?.data ?? [];
  const sourcesFrom = mappings.filter((m) => m.buyerTenantId === tenantId);
  const supplies = mappings.filter((m) => m.warehouseTenantId === tenantId);
  const storeLocations = locations.filter((l) => !isWarehouseType(l.type));
  const otherWarehouses = (warehouseData?.data ?? []).filter((w) => w.tenantId !== tenantId);

  const [buyerLocationId, setBuyerLocationId] = useState('');
  const [warehouseLocationId, setWarehouseLocationId] = useState('');

  // One product-mapping panel per (this tenant → owner tenant) pair.
  const ownerTenants = [...new Map(sourcesFrom.map((m) => [m.warehouseTenantId, m.warehouseTenantName])).entries()].map(
    ([id, name]) => ({ id, name }),
  );

  const add = async () => {
    try {
      await createMapping.mutateAsync({ buyerLocationId, warehouseLocationId });
      setWarehouseLocationId('');
      toast.success('Private warehouse mapped');
    } catch (e) {
      toast.error(errorMessage(e, 'Failed to map private warehouse'));
    }
  };

  const deactivate = async (m: PrivateWarehouseMapping) => {
    if (
      !window.confirm(
        `Stop ${m.buyerTenantName} · ${m.buyerLocationName} sourcing from ${m.warehouseLocationName}? Open orders will still complete.`,
      )
    )
      return;
    try {
      await deactivateMapping.mutateAsync(m.id);
      toast.success('Mapping deactivated');
    } catch (e) {
      toast.error(errorMessage(e, 'Failed to deactivate mapping'));
    }
  };

  const row = (m: PrivateWarehouseMapping) => (
    <li key={m.id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm">
      <span className="min-w-0 truncate">
        <span className="text-muted-foreground">{m.buyerTenantName} · </span>
        {m.buyerLocationName}
      </span>
      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="min-w-0 truncate">
        <span className="text-muted-foreground">{m.warehouseTenantName} · </span>
        {m.warehouseLocationName}
      </span>
      <button
        type="button"
        onClick={() => deactivate(m)}
        disabled={deactivateMapping.isPending}
        className="ml-auto inline-flex shrink-0 items-center gap-1 text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
      >
        <Trash2 className="h-3 w-3" /> Deactivate
      </button>
    </li>
  );

  return (
    <div className="rounded-xl border border-border bg-white p-6">
      <div className="mb-1 flex items-center gap-3">
        <Warehouse className="h-5 w-5 text-muted-foreground" />
        <h2 className="text-lg font-semibold text-foreground">Private Warehouse Mappings</h2>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        Mapped store locations can link a vendor to the private warehouse, see its stock and selling price on product
        detail, and place purchase orders against it.
      </p>

      <h3 className="mb-2 text-sm font-medium">Sources from</h3>
      {sourcesFrom.length === 0 ? (
        <p className="mb-3 text-xs text-muted-foreground">No store location sources from a private warehouse.</p>
      ) : (
        <ul className="mb-3 space-y-2">{sourcesFrom.map(row)}</ul>
      )}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <select value={buyerLocationId} onChange={(e) => setBuyerLocationId(e.target.value)} className={selectCls}>
          <option value="">Store location…</option>
          {storeLocations.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
        <ArrowRight className="h-4 w-4 text-muted-foreground" />
        <select
          value={warehouseLocationId}
          onChange={(e) => setWarehouseLocationId(e.target.value)}
          className={selectCls}
        >
          <option value="">Private warehouse…</option>
          {otherWarehouses.map((w) => (
            <option key={w.id} value={w.id}>
              {w.tenantName} · {w.name}
              {w.isActive ? '' : ' (inactive)'}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={add}
          disabled={!buyerLocationId || !warehouseLocationId || createMapping.isPending}
          className="inline-flex h-9 items-center gap-1 rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          <Link2 className="h-4 w-4" /> Map
        </button>
        {otherWarehouses.length === 0 && (
          <span className="text-xs text-muted-foreground">
            No private warehouses yet — set a location's type to Private Warehouse on its owner's page.
          </span>
        )}
      </div>

      {ownerTenants.map((owner) => (
        <ProductMappingsPanel key={owner.id} buyerTenantId={tenantId} owner={owner} />
      ))}

      <h3 className="mb-2 mt-6 text-sm font-medium">Supplies</h3>
      {supplies.length === 0 ? (
        <p className="text-xs text-muted-foreground">This tenant's private warehouses aren't mapped to anyone.</p>
      ) : (
        <ul className="space-y-2">{supplies.map(row)}</ul>
      )}
    </div>
  );
}

function ProductPicker({
  tenantId,
  label,
  value,
  onChange,
}: {
  tenantId: string;
  label: string;
  value: { id: string; name: string } | null;
  onChange: (p: { id: string; name: string } | null) => void;
}) {
  const [q, setQ] = useState('');
  const { data } = useAdminProductSearch(tenantId, q);
  if (value) {
    return (
      <span className="inline-flex h-9 items-center gap-2 rounded-lg border border-input bg-muted/40 px-2 text-sm">
        {value.name}
        <button type="button" onClick={() => onChange(null)} className="text-xs text-muted-foreground hover:underline">
          change
        </button>
      </span>
    );
  }
  return (
    <div className="relative">
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={label} className={`${selectCls} w-56`} />
      {(data?.data?.length ?? 0) > 0 && (
        <ul className="absolute z-10 mt-1 max-h-56 w-72 overflow-auto rounded-lg border border-border bg-white shadow">
          {data!.data.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => {
                  onChange({ id: p.id, name: p.name });
                  setQ('');
                }}
                className="block w-full px-3 py-1.5 text-left text-sm hover:bg-muted"
              >
                {p.name}
                <span className="ml-1 text-xs text-muted-foreground">{p.primaryBarcode || p.sku || ''}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ProductMappingsPanel({
  buyerTenantId,
  owner,
}: {
  buyerTenantId: string;
  owner: { id: string; name: string };
}) {
  const [open, setOpen] = useState(false);
  const { data } = useAdminProductMappings(buyerTenantId, owner.id, open);
  const { data: preview } = useAdminBarcodePreview(buyerTenantId, owner.id, open);
  const upsert = useUpsertProductMapping();
  const remove = useDeleteProductMapping();
  const [company, setCompany] = useState<{ id: string; name: string } | null>(null);
  const [warehouse, setWarehouse] = useState<{ id: string; name: string } | null>(null);
  const [factor, setFactor] = useState('1');

  const save = async () => {
    if (!company || !warehouse) return;
    try {
      await upsert.mutateAsync({
        companyProductId: company.id,
        warehouseProductId: warehouse.id,
        conversionFactor: Number(factor),
      });
      setCompany(null);
      setWarehouse(null);
      setFactor('1');
      toast.success('Product mapping saved');
    } catch (e) {
      toast.error(errorMessage(e, 'Failed to save product mapping'));
    }
  };

  return (
    <div className="mb-3 rounded-lg border border-border">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted/50"
      >
        <span>
          Product mappings with <span className="font-medium">{owner.name}</span>
        </span>
        <span className="text-xs text-muted-foreground">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-border p-3">
          <p className="text-xs text-muted-foreground">
            Products without a mapping match automatically when their primary barcode is identical (
            {preview?.data?.length ?? 0} barcode match{(preview?.data?.length ?? 0) === 1 ? '' : 'es'}). The conversion
            factor is how many of the warehouse's base units one of this tenant's base units represents.
          </p>
          {(data?.data ?? []).length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-xs text-muted-foreground">
                  <th className="py-1.5 text-left font-medium">This tenant's product</th>
                  <th className="py-1.5 text-left font-medium">{owner.name} product</th>
                  <th className="py-1.5 text-right font-medium">Factor</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data!.data.map((m) => (
                  <tr key={m.id} className="border-b border-border last:border-0">
                    <td className="py-1.5">{m.companyProductName}</td>
                    <td className="py-1.5">{m.warehouseProductName}</td>
                    <td className="py-1.5 text-right tabular-nums">{Number(m.conversionFactor)}</td>
                    <td className="py-1.5 text-right">
                      <button
                        type="button"
                        onClick={() => remove.mutate(m.id)}
                        className="text-xs font-medium text-red-600 hover:underline"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <ProductPicker
              tenantId={buyerTenantId}
              label="This tenant's product…"
              value={company}
              onChange={setCompany}
            />
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <ProductPicker
              tenantId={owner.id}
              label={`${owner.name} product…`}
              value={warehouse}
              onChange={setWarehouse}
            />
            <input
              type="number"
              min="0.001"
              step="any"
              value={factor}
              onChange={(e) => setFactor(e.target.value)}
              className={`${selectCls} w-20`}
              title="Conversion factor"
            />
            <button
              type="button"
              onClick={save}
              disabled={!company || !warehouse || !(Number(factor) > 0) || upsert.isPending}
              className="h-9 rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
